import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Calcula la fecha límite para considerar un hashtag como antiguo
 * @param daysOld Número de días para considerar un hashtag como antiguo
 * @returns Date objeto fecha límite
 */
const getOldDate = (daysOld: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - daysOld);
  return date;
};

/**
 * Limpia hashtags antiguos o poco usados de la base de datos
 * @param minCount Contador mínimo para mantener un hashtag (por defecto 3)
 * @param daysOld Días desde el último uso para considerar un hashtag como antiguo (por defecto 30)
 * @returns Número de hashtags eliminados
 */
export const cleanupOldHashtags = async (minCount: number = 3, daysOld: number = 30): Promise<number> => {
  try {
    const oldDate = getOldDate(daysOld);

    // Consultar hashtags con poco uso y antiguos
    const hashtagsQuery = query(
      collection(db, 'trendingHashtags'),
      where('count', '<', minCount)
    );

    const snapshot = await getDocs(hashtagsQuery);
    let deletedCount = 0;

    // Procesar cada hashtag encontrado
    const deletePromises = snapshot.docs.map(async (docSnapshot) => {
      const data = docSnapshot.data();

      // Verificar si el hashtag es antiguo
      if (data.lastUsed) {
        const lastUsed = data.lastUsed.toDate ? data.lastUsed.toDate() : new Date(data.lastUsed);

        // Si el hashtag es antiguo y tiene poco uso, eliminarlo
        if (lastUsed < oldDate) {
          await deleteDoc(doc(db, 'trendingHashtags', docSnapshot.id));
          deletedCount++;
          return true;
        }
      }
      return false;
    });

    // Esperar a que todas las operaciones de eliminación terminen
    await Promise.all(deletePromises);

    console.log(`Se eliminaron ${deletedCount} hashtags antiguos o poco usados`);
    return deletedCount;
  } catch (error) {
    console.error('Error al limpiar hashtags antiguos:', error);
    throw new Error('No se pudieron limpiar los hashtags antiguos');
  }
};

/**
 * Programar la limpieza automática de hashtags
 * Esta función debería ser llamada cuando la aplicación se inicia
 */
export const scheduleHashtagCleanup = (): NodeJS.Timeout => {
  // Ejecutar la limpieza cada semana (en milisegundos)
  const CLEANUP_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 días

  console.log('Programando limpieza automática de hashtags...');

  // Programar la ejecución periódica
  const intervalId = setInterval(async () => {
    try {
      console.log('Iniciando limpieza automática de hashtags...');
      const deletedCount = await cleanupOldHashtags();
      console.log(`Limpieza completada. Se eliminaron ${deletedCount} hashtags.`);
    } catch (error) {
      console.error('Error durante la limpieza automática de hashtags:', error);
    }
  }, CLEANUP_INTERVAL);

  return intervalId;
};
